import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatDOP, formatDate } from "@/lib/format";

export default function CustomerStatement() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/customers").then(({ data }) => setCustomers(data || [])); }, []);

  const search = async () => {
    if (!customerId) return toast.error("Selecciona un cliente");
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const { data: res } = await api.get(`/customers/${customerId}/statement?${params}`);
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!customerId) return;
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const res = await api.get(`/customers/${customerId}/statement/pdf?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `estado_cuenta_${data?.customer?.name || "cliente"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const selectedCust = useMemo(
    () => customers.find((c) => c.id === customerId),
    [customers, customerId]
  );

  return (
    <PageContainer>
      <PageHeader
        title="Estados de cuenta"
        subtitle="Consulta el saldo y movimientos contables de un cliente."
        testid="statement-header"
      />

      <div className="bg-card border border-border rounded-md p-4 grid grid-cols-1 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2">
          <Label className="overline">Cliente</Label>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger className="mt-1" data-testid="statement-customer">
              <SelectValue placeholder="Selecciona un cliente" />
            </SelectTrigger>
            <SelectContent>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.rnc_cedula ? ` · ${c.rnc_cedula}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Desde</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1" data-testid="statement-from" />
        </div>
        <div>
          <Label className="overline">Hasta</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1" data-testid="statement-to" />
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={search} disabled={loading} className="bg-primary font-bold flex-1" data-testid="statement-search">
            {loading ? "Cargando..." : "Ver"}
          </Button>
          <Button variant="outline" onClick={downloadPdf} disabled={!data} title="Descargar PDF" data-testid="statement-pdf">
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {data && (
        <div className="mt-6 bg-card border border-border rounded-md p-4" data-testid="statement-result">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div>
              <div className="text-lg font-bold">{data.customer.name}</div>
              {selectedCust?.credit_days > 0 && (
                <div className="text-xs text-muted-foreground">
                  Crédito: Neto {selectedCust.credit_days} días
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs overline text-muted-foreground">Saldo actual</div>
              <div
                className={`text-2xl data-cell font-bold ${
                  data.closing_balance > 0
                    ? "text-foreground"           // (Item 10) positivo en NEGRO
                    : data.closing_balance < 0
                    ? "text-emerald-700 dark:text-emerald-400"  // a favor del cliente
                    : "text-muted-foreground"
                }`}
                data-testid="statement-closing"
              >
                {formatDOP(data.closing_balance)}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="overline border-b border-border">
                <tr>
                  <th className="text-left p-2">Fecha</th>
                  <th className="text-left p-2">Doc</th>
                  <th className="text-left p-2">Descripción</th>
                  <th className="text-right p-2">Debe</th>
                  <th className="text-right p-2">Haber</th>
                  <th className="text-right p-2">Saldo</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/60 bg-muted/20">
                  <td className="p-2 text-xs text-muted-foreground">—</td>
                  <td className="p-2 text-xs text-muted-foreground">—</td>
                  <td className="p-2 italic text-xs">Saldo inicial</td>
                  <td className="p-2 text-right"></td>
                  <td className="p-2 text-right"></td>
                  <td className="p-2 text-right data-cell font-bold">{formatDOP(data.opening_balance)}</td>
                </tr>
                {data.movements.map((m, idx) => (
                  <tr key={idx} className="border-b border-border/40 hover:bg-accent">
                    <td className="p-2 text-xs">{formatDate(m.date)}</td>
                    <td className="p-2 data-cell text-xs">{m.doc_number || "—"}</td>
                    <td className="p-2">{m.description}</td>
                    <td className="p-2 text-right data-cell">{m.debit ? formatDOP(m.debit) : ""}</td>
                    <td className="p-2 text-right data-cell text-emerald-700">{m.credit ? formatDOP(m.credit) : ""}</td>
                    <td className="p-2 text-right data-cell font-semibold">{formatDOP(m.balance)}</td>
                  </tr>
                ))}
                {data.movements.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-muted-foreground italic">Sin movimientos en el período.</td></tr>
                )}
                <tr className="border-t-2 border-border bg-muted/40 font-bold">
                  <td className="p-2"></td>
                  <td className="p-2"></td>
                  <td className="p-2 text-right">Totales del período:</td>
                  <td className="p-2 text-right data-cell">{formatDOP(data.total_debit)}</td>
                  <td className="p-2 text-right data-cell text-emerald-700">{formatDOP(data.total_credit)}</td>
                  <td className="p-2 text-right data-cell text-primary">{formatDOP(data.closing_balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data && (
        <div className="mt-6 border border-dashed border-border rounded-md p-8 text-center text-muted-foreground">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Selecciona un cliente y presiona <b>Ver</b> para consultar su estado de cuenta.
        </div>
      )}
    </PageContainer>
  );
}
